// composables/useLoadingError.ts
import { ref, computed } from 'vue';

interface ErrorDetails {
  type: 'general_error' | 'program_issue';
  suggestions: string[];
}

// No response at all (server down/unreachable/timed out) — affects every program.
const GENERIC_ERROR_MESSAGE =
  "We're having trouble generating your plan right now. Our team has been notified and is working on it. Please try again in a few minutes.";

// Server responded, so it's up — the failure is specific to this program/request.
const PROGRAM_ISSUE_MESSAGE =
  "We ran into a problem generating a plan for this specific program. Our team has been notified and is looking into it. You're welcome to keep exploring other programs in the meantime.";

const loadingSteps = [
  'Getting things ready...',
  'Looking up your courses...',
  'Checking program requirements...',
  'Putting it all together...',
  'Building your transfer plan...',
  'Almost there...',
];

const patienceMessages = [
  "Hang tight — this one's a bit more complex, but we're on it!",
  "Still working... detailed plans just need a little extra love.",
  "Almost there! We promise it'll be worth the wait.",
];

export function useLoadingError() {
  const loading = ref(false);
  const loadingMsg = ref('');
  const loadingProgress = ref(0);
  const currentLoadingStep = ref(0);
  const patienceMessage = ref('');
  const startTime = ref<number | null>(null);
  const estimatedTime = ref(30);
  
  const error = ref(false);
  const errorMsg = ref('');
  const errorDetails = ref<ErrorDetails | null>(null);
  const showErrorDetails = ref(false);
  const retryCount = ref(0);
  const maxRetries = ref(3);

  const canRetry = computed(() => retryCount.value < maxRetries.value);

  const startLoadingWithProgress = () => {
    loading.value = true;
    error.value = false;
    loadingProgress.value = 0;
    currentLoadingStep.value = 0;
    patienceMessage.value = '';
    retryCount.value = 0;
    startTime.value = Date.now();
    loadingMsg.value = loadingSteps[0];
    animateProgress();
  };

  const updateLoadingProgress = (step: number, message: string) => {
    currentLoadingStep.value = step;
    loadingMsg.value = message;
    loadingProgress.value = Math.min(95, (step / loadingSteps.length) * 100);
  };

  const animateProgress = () => {
    if (!loading.value || !startTime.value) return;

    const elapsed = (Date.now() - startTime.value) / 1000;
    const progressByTime = Math.min(85, (elapsed / estimatedTime.value) * 100);

    loadingProgress.value = Math.max(loadingProgress.value, progressByTime);

    const baseMessage = loadingSteps[currentLoadingStep.value] || 'Processing...';
    loadingMsg.value = baseMessage;

    // Auto-advance steps so the UI doesn't feel frozen during long API calls.
    // updateLoadingProgress() can still jump ahead; these are lower-bound advances only.
    if (elapsed > 35 && currentLoadingStep.value < 4) {
      currentLoadingStep.value = 4;
    } else if (elapsed > 60 && currentLoadingStep.value < 5) {
      currentLoadingStep.value = 5;
    }

    if (elapsed >= 45) {
      patienceMessage.value = patienceMessages[2];
    } else if (elapsed >= 20) {
      patienceMessage.value = patienceMessages[1];
    } else if (elapsed >= 10) {
      patienceMessage.value = patienceMessages[0];
    }

    setTimeout(() => animateProgress(), 1000);
  };

  const completeLoading = () => {
    loadingProgress.value = 100;
    loading.value = false;
    patienceMessage.value = '';
  };

  const handleError = (err: any) => {
    console.error("Operation failed:", err);
    loading.value = false;
    error.value = true;
    retryCount.value++;

    // 502/503/504 are generated by an intermediary (proxy/load balancer) when it
    // can't reach the app at all — treat them like "no response", not like an
    // answer from Julia itself.
    const status = err.response?.status;
    const isGatewayFailure = status === 502 || status === 503 || status === 504;

    if (err.response && !isGatewayFailure) {
      // The app itself responded, so it's up — this request/program hit a specific issue.
      errorMsg.value = PROGRAM_ISSUE_MESSAGE;
      errorDetails.value = {
        type: 'program_issue',
        suggestions: [
          'This program combination may have a data or requirements issue',
          'You can continue exploring other programs while we look into it'
        ]
      };
    } else {
      // No response at all, or a gateway-level failure — the server itself is unreachable.
      errorMsg.value = GENERIC_ERROR_MESSAGE;
      errorDetails.value = {
        type: 'general_error',
        suggestions: [
          'Wait a few minutes and try again',
          'Contact support if the problem persists'
        ]
      };
    }
  };

  const resetError = () => {
    error.value = false;
    errorMsg.value = '';
    errorDetails.value = null;
    showErrorDetails.value = false;
  };

  const toggleErrorDetails = () => {
    showErrorDetails.value = !showErrorDetails.value;
  };

  const delay = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  return {
    // State
    loading,
    loadingMsg,
    loadingProgress,
    currentLoadingStep,
    loadingSteps,
    patienceMessage,
    error,
    errorMsg,
    errorDetails,
    showErrorDetails,
    retryCount,
    maxRetries,
    canRetry,

    // Methods
    startLoadingWithProgress,
    updateLoadingProgress,
    completeLoading,
    handleError,
    resetError,
    toggleErrorDetails,
    delay,
  };
}